import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  published: "bg-[#e6f4ea] text-[#1e7e34]",
  draft: "bg-[#f4f4f4] text-[#555555]",
  scheduled: "bg-[#fff4e5] text-[#b25e09]",
};

const AdminPodcastListPage = () => {
  const [episodes, setEpisodes] = useState(null);

  const load = () =>
    axios.get(`${API}/admin/podcast`, { withCredentials: true }).then((res) => setEpisodes(res.data));

  useEffect(() => {
    load();
  }, []);

  async function toggleStatus(ep) {
    const next = ep.status === "published" ? "draft" : "published";
    await axios.put(`${API}/admin/podcast/${ep.id}`, { ...ep, status: next }, { withCredentials: true });
    load();
  }

  async function remove(ep) {
    if (!window.confirm(`Delete "${ep.title}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/podcast/${ep.id}`, { withCredentials: true });
    load();
  }

  return (
    <div data-testid="admin-podcast-list">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-disp text-3xl text-[#121212]">Podcast Episodes</h1>
        <Link
          to="/admin/podcast/new"
          data-testid="admin-podcast-new-button"
          className="px-5 py-3 bg-[#121212] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#e02020] transition-colors rounded-none"
        >
          New Episode
        </Link>
      </div>

      {episodes === null ? (
        <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555]">Loading…</p>
      ) : episodes.length === 0 ? (
        <div className="bg-white border border-[#e5e5e5] p-10 text-center">
          <p className="text-sm text-[#555555] mb-4">
            No episodes yet. Add the first episode when you are ready.
          </p>
          <Link
            to="/admin/podcast/new"
            className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020]"
          >
            Add an Episode →
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e5e5] divide-y divide-[#e5e5e5]">
          {episodes.map((ep) => (
            <div
              key={ep.id}
              data-testid={`admin-episode-row-${ep.id}`}
              className="p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between"
            >
              <div className="min-w-0">
                <p className="font-disp text-lg text-[#121212] truncate">
                  {ep.episode_number ? `Ep. ${ep.episode_number} — ` : ""}{ep.title}
                </p>
                <p className="text-xs font-mono text-[#6b6b6b]">{ep.guest || "No guest listed"}</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className={`text-xs font-mono uppercase tracking-[.14em] px-2 py-1 ${STATUS_STYLES[ep.status] || STATUS_STYLES.draft}`}>
                  {ep.status}
                </span>
                <button
                  type="button"
                  onClick={() => toggleStatus(ep)}
                  data-testid={`admin-episode-toggle-${ep.id}`}
                  className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020]"
                >
                  {ep.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <Link
                  to={`/admin/podcast/${ep.id}/edit`}
                  data-testid={`admin-episode-edit-${ep.id}`}
                  className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020]"
                >
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={() => remove(ep)}
                  data-testid={`admin-episode-delete-${ep.id}`}
                  className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b] hover:text-[#e02020]"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPodcastListPage;
